import { CatalogFlow, CatalogNode, nextCatalogId, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice, OLIMEX_GPIO_INPUT_LINES } from './graph';
import {
  CatalogWizardArgs,
  ensureController,
  ensureDevice,
  ensurePoint,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

type GpioLine = {
  key: string;
  pin: number;
  mode: string;
  edge: string;
  inverted: boolean;
};

/** Zigbee analog is one number. Offsets keep IN1–IN4 apart from the button. */
const ZIGBEE_OFFSET: Record<string, number> = {
  button: 0,
  in1: 10,
  in2: 20,
  in3: 30,
  in4: 40,
};

function linesOf(device: GraphDevice): GpioLine[] {
  const raw = Array.isArray(device.lines) && device.lines.length
    ? device.lines
    : OLIMEX_GPIO_INPUT_LINES;
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      key: String(row.key || '').trim(),
      pin: Number(row.pin),
      mode: String(row.mode || 'input'),
      edge: String(row.edge || 'change'),
      inverted: row.inverted === true,
    };
  }).filter((line) => line.key && Number.isFinite(line.pin));
}

export function applyGpioInputHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const instance: GraphDevice = {
    ...device,
    id: String(device.id || 'inputs').trim() || 'inputs',
    type: 'gpio_input',
    enable: device.enable !== false,
    lines: linesOf(device),
  };
  return {
    ...values,
    devices: upsertDevice(values, instance),
  };
}

export function mergeGpioInputCatalog(doc: CatalogFlow, args: CatalogWizardArgs): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'inputs').trim() || 'inputs';
  const controller = ensureController(state, args);
  const device = ensureDevice(state, {
    of,
    deviceType: 'gpio_input',
    x: state.x0 + 220,
    y: state.y0,
  });
  link(state, `${controller.id}.host`, `${device.id}.read`);
  const zigbee = ensureDevice(state, {
    of: 'zigbee',
    deviceType: 'zigbee',
    x: state.x0 + 980,
    y: state.y0,
  });
  linesOf(args.device).forEach((line, index) => {
    const y = state.y0 + index * 120;
    const point = ensurePoint(state, {
      of,
      key: line.key,
      poll: false,
      x: state.x0 + 460,
      y,
      deviceId: device.id,
    });
    wireToMqtt(state, {
      point,
      topic: `{base}/${of}/${line.key}`,
      y,
      xCalc: state.x0 + 700,
      xMqtt: state.x0 + 980,
      from: 'event',
    });
    const offset = ZIGBEE_OFFSET[line.key] ?? 0;
    if (offset === 0) {
      link(state, `${point.id}.event`, `${zigbee.id}.in`);
      return;
    }
    const calc: CatalogNode = {
      id: nextCatalogId('calc', state.nodes),
      type: 'calc',
      x: state.x0 + 720,
      y,
      op: '+',
      value: offset,
    };
    state.nodes.push(calc);
    link(state, `${point.id}.event`, `${calc.id}.in`);
    link(state, `${calc.id}.out`, `${zigbee.id}.in`);
  });
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
